import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import type { Customization } from '~/types/Customization'
import type { SuggestionWithCustomization } from '~/components/organisms/AICustomization/types'

export interface UseAICustomizationPanelParams {
  opened: boolean
  suggestions: SuggestionWithCustomization[] | null
  onApply: (customization: Customization) => void
  onClose: () => void
  onGenerate: (prompt: string, selectedColorIds: string[]) => void
  onClearError: () => void
  // --- 外部制御用（タブ切替間で保持したいエフェメラル状態） ---
  promptExternal?: string
  onPromptExternalChange?: (v: string) => void
  selectedColorIdsExternal?: Set<string>
  onToggleColorExternal?: (id: string) => void
  onResetSelectionExternal?: () => void
  onHasGeneratedOnceChange?: (v: boolean) => void
}
export interface UseAICustomizationPanelResult {
  // State
  prompt: string
  currentViewIndex: number
  selectedColorIds: Set<string>
  mobileMode: 'customize' | 'preview'

  // Actions
  setPrompt: (prompt: string) => void
  setCurrentViewIndex: (index: number) => void
  handleGenerate: () => void
  handleReset: () => void
  handleRetry: () => void
  handleApply: () => void
  handleClose: () => void
  handleSampleClick: (samplePrompt: string) => void
  toggleColor: (colorId: string) => void
  resetColorSelection: () => void
  setMobileMode: (mode: 'customize' | 'preview') => void
}

export function useAICustomizationPanel({
  opened,
  suggestions,
  onApply,
  onClose,
  onGenerate,
  onClearError,
  promptExternal,
  onPromptExternalChange,
  selectedColorIdsExternal,
  onToggleColorExternal,
  onResetSelectionExternal,
  onHasGeneratedOnceChange
}: UseAICustomizationPanelParams): UseAICustomizationPanelResult {
  const isPromptControlled =
    typeof promptExternal === 'string' && !!onPromptExternalChange
  const [promptState, setPromptState] = useState('')
  const prompt = isPromptControlled ? (promptExternal as string) : promptState
  const setPrompt = useCallback(
    (value: string) => {
      if (isPromptControlled && onPromptExternalChange) {
        onPromptExternalChange(value)
      } else {
        setPromptState(value)
      }
    },
    [isPromptControlled, onPromptExternalChange]
  )
  const [currentViewIndex, setCurrentViewIndex] = useState(0)
  const isColorControlled = !!selectedColorIdsExternal
  const [selectedColorIdsState, setSelectedColorIdsState] = useState<
    Set<string>
  >(new Set())
  const selectedColorIds = isColorControlled
    ? (selectedColorIdsExternal as Set<string>)
    : selectedColorIdsState
  // single suggestion flow: no selection index
  const [mobileMode, setMobileMode] = useState<'customize' | 'preview'>(
    'customize'
  )

  // Prevent repeated auto-selection loops
  const hasAutoSelectedRef = useRef(false)

  // Reset state on dialog open
  useEffect(() => {
    if (opened) {
      setCurrentViewIndex(0)
      if (!isColorControlled) {
        setSelectedColorIdsState(new Set())
      }
      setMobileMode('customize')
      hasAutoSelectedRef.current = false
    }
  }, [opened, isColorControlled])

  // When suggestion is generated, move to preview on mobile once
  useEffect(() => {
    if (!hasAutoSelectedRef.current && suggestions && suggestions.length > 0) {
      hasAutoSelectedRef.current = true
      setMobileMode('preview')
    }
  }, [suggestions])

  const handleGenerate = useCallback(() => {
    if (prompt.trim() || selectedColorIds.size > 0) {
      onGenerate(
        prompt.trim() || '選択された色を使用してカスタマイズ',
        Array.from(selectedColorIds)
      )
      if (mobileMode === 'customize') {
        setMobileMode('preview')
      }
    }
  }, [prompt, selectedColorIds, onGenerate, mobileMode])

  const handleReset = useCallback(() => {
    setPrompt('')
    if (isColorControlled) {
      onResetSelectionExternal?.()
    } else {
      setSelectedColorIdsState(new Set())
    }
    onClearError()
    onHasGeneratedOnceChange?.(false)
  }, [
    onClearError,
    isColorControlled,
    onResetSelectionExternal,
    onHasGeneratedOnceChange,
    setPrompt
  ])

  const handleRetry = useCallback(() => {
    if (prompt.trim() || selectedColorIds.size > 0) {
      onGenerate(
        prompt.trim() || '選択された色を使用してカスタマイズ',
        Array.from(selectedColorIds)
      )
      if (mobileMode === 'customize') {
        setMobileMode('preview')
      }
    }
  }, [prompt, selectedColorIds, onGenerate, mobileMode])

  const handleApply = useCallback(() => {
    if (suggestions?.[0]) {
      const selectedSuggestion = suggestions[0]
      onApply(selectedSuggestion.customization)
      onClose()
      setPrompt('')
    }
  }, [suggestions, onApply, onClose, setPrompt])

  const handleClose = useCallback(() => {
    // タブを閉じるだけ。エフェメラル状態は維持する（リロードで初期化）
    onClose()
    setMobileMode('customize')
    onClearError()
  }, [onClose, onClearError])

  const handleSampleClick = useCallback(
    (samplePrompt: string) => {
      setPrompt(samplePrompt)
    },
    [setPrompt]
  )

  // History UI removed; favorites and restore/delete are no longer supported

  const toggleColor = useCallback(
    (colorId: string) => {
      if (isColorControlled) {
        onToggleColorExternal?.(colorId)
        return
      }
      setSelectedColorIdsState((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(colorId)) {
          newSet.delete(colorId)
        } else {
          newSet.add(colorId)
        }
        return newSet
      })
    },
    [isColorControlled, onToggleColorExternal]
  )

  const resetColorSelection = useCallback(() => {
    if (isColorControlled) {
      onResetSelectionExternal?.()
      return
    }
    setSelectedColorIdsState(new Set())
  }, [isColorControlled, onResetSelectionExternal])

  return {
    // State
    prompt,
    currentViewIndex,
    selectedColorIds,
    mobileMode,

    // Actions
    setPrompt,
    setCurrentViewIndex,
    handleGenerate,
    handleReset,
    handleRetry,
    handleApply,
    handleClose,
    handleSampleClick,
    toggleColor,
    resetColorSelection,
    setMobileMode
  }
}
